import { useMemo } from 'react';
import { useActiveUsers, useEndorserUsers, useOpenItems, useItemCategories, useItemSubcategories, useWorkflowStages, useAllChoices, useProjects, useCustomFields, useRiskMatrix } from './useQueries';
import { createFieldConfig, getFieldConfig } from '../config/fieldConfig';

/**
 * Hook that provides field configurations with all necessary data loaded
 * This centralizes all field configuration logic and data dependencies
 */
export const useFieldConfigs = ({
  currentCategoryId = null,
  onCategoryChange = null,
  projectId = null,
  currentProjectId = null, // Task order already on the item; kept selectable even if expired
  currentItemId = null // Add current item ID parameter
} = {}) => {
  // Fetch all necessary data
  const { data: activeUsers = [] } = useActiveUsers();
  const { data: endorserUsers = [] } = useEndorserUsers();
  const { data: openItems = [] } = useOpenItems(); // Changed from endorsedItems
  const { data: categories = [] } = useItemCategories();
  const { data: subcategories = [] } = useItemSubcategories(currentCategoryId);
  const { data: workflowStages = [] } = useWorkflowStages();
  const { data: allChoices = {} } = useAllChoices();
  const { data: projects = [] } = useProjects();
  
  // Get custom fields with options
  // Using projectId ensures same query cache key as CustomFieldsRenderer
  const { data: customFieldsData = [] } = useCustomFields(projectId, true);
  
  // Get risk matrix configuration for dynamic dependencies
  const { data: riskMatrixConfig } = useRiskMatrix();
  
  // Extract choice arrays from the combined response
  // Note: cost, schedule, and probability choices are now handled as custom fields
  const { 
    detectabilityChoices = [], 
    originatingDisciplineChoices = [],
    rootCauseCategoryChoices = [],
    opportunityThreatChoices = [],
    typeChoices = [],
    riskTypeChoices = [],
    assignedDisciplineChoices = []
  } = allChoices;

  // Create field configurations with all data
  const fieldConfigs = useMemo(() => {
    const baseConfigs = createFieldConfig({
      activeUsers,
      endorserUsers,
      openItems, // Changed from endorsedItems
      categories,
      subcategories,
      workflowStages,
      projects,
      detectabilityChoices,
      originatingDisciplineChoices,
      rootCauseCategoryChoices,
      opportunityThreatChoices,
      typeChoices,
      riskTypeChoices,
      assignedDisciplineChoices,
      currentCategoryId,
      currentProjectId,
      onCategoryChange,
      riskMatrixConfig, // Add risk matrix configuration for dynamic dependencies
      currentItemId // Pass current item ID
    });
    
    
    /**
     * Custom Fields Integration
     * 
     * Custom fields are defined in the database and fetched dynamically.
     * They are merged into the field configuration system with keys like `custom_field_{id}`.
     * Supported types: text, textarea, select, yesno
     * Custom field values are stored in `item_custom_field_values` table, not the `items` table.
     * Components should use `useFieldConfigs` to get the merged configuration.
     */
    // Add custom fields to the configuration
    if (customFieldsData && customFieldsData.length > 0) {
      customFieldsData.forEach(customField => {
        const fieldKey = `custom_field_${customField.id}`;
        
        // Skip cost_impact field - it's handled specially by CustomFieldsRenderer
        // using project-specific configurations, not database options
        // This field is strictly project-dependent and should NOT be rendered without a project context
        if (customField.name === 'cost_impact') {
          // Register for metadata purposes only - actual rendering is handled by CustomFieldsRenderer
          baseConfigs[fieldKey] = {
            label: customField.label,
            type: 'select',
            isCustom: true,
            isClearable: true,
            requiresProject: true, // Flag indicating this field requires project context
            dataSource: 'projectConfig', // Marker that this uses project-specific configuration
            options: [], // Empty - actual options come from project-specific cost impact config
            ...(customField.tooltip && { tooltip: customField.tooltip })
          };
          return;
        }
        
        let fieldType = customField.type;
        let options = undefined;
        
        // Map custom field types and handle options
        if (customField.type === 'select' && customField.options) {
          options = customField.options.map(option => {
            const rawLabel = option.option_label ?? option.label ?? '';
            const score = option.score;
            const labelWithScore = (score !== null && score !== undefined && score !== '')
              ? `${score} - ${rawLabel}`
              : rawLabel;

            return {
              value: option.option_value,
              label: labelWithScore,
              score,
              rawLabel
            };
          }) || []; // Ensure empty array if no options
        }
        
        baseConfigs[fieldKey] = {
          label: customField.label,
          type: fieldType,
          isCustom: true,
          // A yesno has no empty state, so there is nothing to clear it to.
          isClearable: customField.type === 'select',
          dataSource: 'customFields',
          ...(fieldType === 'yesno' && {
            options: [
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' }
            ],
            defaultValue: 'No'
          }),
          ...(customField.tooltip && { tooltip: customField.tooltip }),
          ...(options && { options })
        };
      });
    }
    
    return baseConfigs;
  }, [
    activeUsers,
    endorserUsers,
    openItems, // Changed from endorsedItems
    categories,
    subcategories,
    workflowStages,
    projects,
    detectabilityChoices,
    originatingDisciplineChoices,
    rootCauseCategoryChoices,
    opportunityThreatChoices,
    typeChoices,
    riskTypeChoices,
    assignedDisciplineChoices,
    currentCategoryId,
    currentProjectId,
    onCategoryChange,
    customFieldsData,
    riskMatrixConfig,
    currentItemId
  ]);

  // Helper function to get a specific field config
  const getField = (fieldId) => {
    return fieldConfigs[fieldId] || getFieldConfig(fieldId, {
      activeUsers,
      endorserUsers,
      openItems, // Changed from endorsedItems
      categories,
      subcategories,
      workflowStages,
      projects,
      detectabilityChoices,
      originatingDisciplineChoices,
      rootCauseCategoryChoices,
      opportunityThreatChoices,
      typeChoices,
      riskTypeChoices,
      assignedDisciplineChoices,
      currentCategoryId,
      onCategoryChange
    });
  };

  // Helper function to get multiple field configs
  const getFields = (fieldIds) => {
    return fieldIds.map(fieldId => ({
      fieldId,
      config: getField(fieldId)
    }));
  };

  // Helper function to get all configurations for a specific data source
  const getFieldsByDataSource = (dataSource) => {
    return Object.entries(fieldConfigs)
      .filter(([, config]) => config.dataSource === dataSource)
      .map(([fieldId, config]) => ({ fieldId, config }));
  };

  return {
    // Main configurations
    fieldConfigs,
    
    // Helper functions
    getField,
    getFields,
    getFieldsByDataSource,
    
    // Raw data (for cases where direct access is needed)
    data: {
      activeUsers,
      endorserUsers,
      openItems, // Changed from endorsedItems
      categories,
      subcategories,
      workflowStages
    },
    
    // Choices data for calculations
    choicesData: {
      detectabilityChoices,
      originatingDisciplineChoices,
      rootCauseCategoryChoices,
      opportunityThreatChoices,
      typeChoices,
      riskTypeChoices,
      assignedDisciplineChoices
    },
    
    // Loading states
    isLoading: {
      users: !activeUsers.length,
      categories: !categories.length,
      workflowStages: !workflowStages.length
    }
  };
};

/**
 * Helper function to check if a fieldId is a custom field
 * @param {string} fieldId - The field ID to check
 * @returns {boolean} True if the fieldId starts with 'custom_field_'
 */
export const isCustomField = (fieldId) => {
  return typeof fieldId === 'string' && fieldId.startsWith('custom_field_');
};

/**
 * Simplified hook for getting a single field configuration
 */
export const useFieldConfig = (fieldId, options = {}) => {
  const { getField } = useFieldConfigs(options);
  return getField(fieldId);
};